import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import DirectionsCarIcon from "@mui/icons-material/DirectionsCar"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import BuildIcon from "@mui/icons-material/Build"

import MainLayout from "../../layouts/MainLayout"

import {
  createVehicle,
  deactivateVehicle,
  getVehicleFormData,
  getVehicles,
  updateVehicle,
} from "../../services/vehicleService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultForm = {
  plate_number: "",
  brand: "",
  model: "",
  vehicle_type: "camion",
  capacity_units: "",
  fuel_consumption_l_100km: "",
  status: "available",
  is_active: true,
  notes: "",
}


const statusLabels = {
  available: "Disponible",
  in_delivery: "En livraison",
  maintenance: "Maintenance",
  inactive: "Inactif",
}


const statusColors = {
  available: "success",
  in_delivery: "info",
  maintenance: "warning",
  inactive: "default",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatNumber(
  value,
  maximumFractionDigits = 0
) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      maximumFractionDigits,
    }
  )
}


function getApiErrorMessage(
  error,
  fallback = "Action impossible."
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


function getVehicleTypeLabel(
  value,
  vehicleTypes = []
) {
  return (
    vehicleTypes.find(
      (item) =>
        item.value === value
    )?.label ||
    value ||
    "-"
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.2,
          sm: 1.3,
        },

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",

        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform:
            "translateY(-1px)",

          boxShadow:
            "0 9px 24px rgba(15, 23, 42, 0.065)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.2,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,

            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.75,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",

              textOverflow: "ellipsis",

              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function VehiclesPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    vehicles,
    setVehicles,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    formData,
    setFormData,
  ] = useState({
    statuses: [],
    vehicle_types: [],
    permissions: {},
  })


  // =======================================================
  // UI STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedVehicle,
    setSelectedVehicle,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState({
    ...defaultForm,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    status: "",
    is_active: "",
  })


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // PERMISSIONS
  // =======================================================

  const permissions =
    formData.permissions || {}


  // =======================================================
  // SNACKBAR HELPERS
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (current) => ({
        ...current,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getVehicleFormData()

        setFormData({
          statuses:
            data.statuses || [],

          vehicle_types:
            data.vehicle_types || [],

          permissions:
            data.permissions || {},
        })
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // LOAD VEHICLES
  // =======================================================

  const loadVehicles =
    async (
      customFilters = null
    ) => {
      try {
        setLoading(true)

        const params =
          customFilters ||
          filters

        const data =
          await getVehicles(
            params
          )

        setVehicles(
          Array.isArray(
            data?.items
          )
            ? data.items
            : []
        )

        setSummary(
          data?.summary || {}
        )

        setPaginationModel(
          (previous) => ({
            ...previous,

            page: 0,

            pageSize:
              ROWS_PER_PAGE,
          })
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les véhicules."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadFormData()

    loadVehicles({
      search: "",
      status: "",
      is_active: "",
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setForm({
      ...defaultForm,
    })

    setSelectedVehicle(
      null
    )
  }


  // =======================================================
  // CREATE
  // =======================================================

  const openCreateDialog =
    () => {
      resetForm()
      setOpen(true)
    }


  // =======================================================
  // EDIT
  // =======================================================

  const openEditDialog =
    (vehicle) => {
      setSelectedVehicle(
        vehicle
      )

      setForm({
        plate_number:
          vehicle.plate_number ||
          "",

        brand:
          vehicle.brand ||
          "",

        model:
          vehicle.model ||
          "",

        vehicle_type:
          vehicle.vehicle_type ||
          "camion",

        capacity_units:
          vehicle.capacity_units !==
            null &&
          vehicle.capacity_units !==
            undefined
            ? Number(
                vehicle.capacity_units
              )
            : "",

        fuel_consumption_l_100km:
          vehicle.fuel_consumption_l_100km !==
            null &&
          vehicle.fuel_consumption_l_100km !==
            undefined
            ? Number(
                vehicle.fuel_consumption_l_100km
              )
            : "",

        status:
          vehicle.status ||
          "available",

        is_active:
          typeof vehicle.is_active ===
          "boolean"
            ? vehicle.is_active
            : true,

        notes:
          vehicle.notes ||
          "",
      })

      setOpen(true)
    }


  // =======================================================
  // CLOSE DIALOG
  // =======================================================

  const closeDialog = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // UPDATE FORM
  // =======================================================

  const updateForm = (
    field,
    value
  ) => {
    setForm(
      (current) => ({
        ...current,

        [field]:
          value,
      })
    )
  }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !form.plate_number?.trim()
    ) {
      showSnackbar(
        "La plaque est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        form.capacity_units
      ) < 0
    ) {
      showSnackbar(
        "La capacité ne peut pas être négative.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        form.fuel_consumption_l_100km
      ) <= 0
    ) {
      showSnackbar(
        "La consommation L/100km est obligatoire et doit être supérieure à 0.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // BUILD PAYLOAD
  // =======================================================

  const buildPayload = () => {
    return {
      plate_number:
        form.plate_number
          .trim()
          .toUpperCase(),

      brand:
        form.brand?.trim() ||
        null,

      model:
        form.model?.trim() ||
        null,

      vehicle_type:
        form.vehicle_type ||
        "camion",

      capacity_units:
        Number(
          form.capacity_units ||
          0
        ),

      fuel_consumption_l_100km:
        Number(
          form.fuel_consumption_l_100km ||
          0
        ),

      status:
        form.status ||
        "available",

      is_active:
        Boolean(
          form.is_active
        ),

      notes:
        form.notes?.trim() ||
        null,
    }
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      try {
        setSaving(true)

        const payload =
          buildPayload()

        if (
          selectedVehicle?.id
        ) {
          await updateVehicle(
            selectedVehicle.id,
            payload
          )

          showSnackbar(
            "Véhicule modifié avec succès."
          )
        } else {
          await createVehicle(
            payload
          )

          showSnackbar(
            "Véhicule créé avec succès."
          )
        }

        /*
         * Fermeture directe :
         * closeDialog() ne doit pas être utilisé ici
         * pendant que saving === true.
         */
        setOpen(false)
        resetForm()

        await loadVehicles()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible d'enregistrer le véhicule."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DEACTIVATE
  // =======================================================

  const handleDeactivate =
    async (vehicle) => {
      const ok =
        window.confirm(
          `Désactiver le véhicule ${vehicle.plate_number} ?`
        )

      if (!ok) {
        return
      }

      try {
        await deactivateVehicle(
          vehicle.id
        )

        showSnackbar(
          "Véhicule désactivé avec succès."
        )

        await loadVehicles()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de désactiver le véhicule."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters =
    async () => {
      const emptyFilters = {
        search: "",
        status: "",
        is_active: "",
      }

      setFilters(
        emptyFilters
      )

      setPaginationModel({
        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })

      await loadVehicles(
        emptyFilters
      )
    }


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedVehicles =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return vehicles.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      vehicles,
      paginationModel.page,
    ])


  // =======================================================
  // KEEP PAGE VALID
  // =======================================================

  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          vehicles.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    vehicles.length,
    paginationModel.page,
  ])


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns = [
    {
      field:
        "plate_number",

      headerName:
        "Plaque",

      width: 125,

      renderCell:
        (params) => (
          <Typography
            variant="body2"
            fontWeight={900}
            color="#0f172a"
            noWrap
          >
            {params.value ||
              "-"}
          </Typography>
        ),
    },


    {
      field:
        "vehicle",

      headerName:
        "Véhicule",

      minWidth: 185,

      flex: 1.2,

      sortable: false,

      renderCell:
        (params) => {
          const row =
            params.row || {}

          return (
            <Stack
              spacing={0.08}
              sx={{
                minWidth: 0,

                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={800}
                noWrap
                title={`${row.brand || ""} ${row.model || ""}`}
              >
                {row.brand || "-"}{" "}
                {row.model || ""}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
              >
                {getVehicleTypeLabel(
                  row.vehicle_type,
                  formData.vehicle_types
                )}
              </Typography>
            </Stack>
          )
        },
    },


    {
      field:
        "capacity_units",

      headerName:
        "Capacité",

      width: 105,

      align: "center",

      headerAlign:
        "center",

      renderCell:
        (params) => (
          <Typography
            variant="body2"
            fontWeight={800}
          >
            {formatNumber(
              params.value
            )}
          </Typography>
        ),
    },


    {
      field:
        "fuel_consumption_l_100km",

      headerName:
        "Conso L/100 km",

      width: 130,

      align: "center",

      headerAlign:
        "center",

      renderCell:
        (params) => (
          <Stack
            spacing={0.05}
            alignItems="center"
          >
            <Typography
              variant="body2"
              fontWeight={900}
            >
              {formatNumber(
                params.value,
                2
              )}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              L/100 km
            </Typography>
          </Stack>
        ),
    },


    {
      field:
        "status",

      headerName:
        "Statut",

      width: 130,

      renderCell:
        (params) => (
          <Chip
            size="small"

            label={
              statusLabels[
                params.value
              ] ||
              params.value ||
              "-"
            }

            color={
              statusColors[
                params.value
              ] ||
              "default"
            }

            sx={{
              fontWeight: 800,
            }}
          />
        ),
    },


    {
      field:
        "is_active",

      headerName:
        "Actif",

      width: 85,

      renderCell:
        (params) => (
          <Chip
            size="small"

            label={
              params.value
                ? "Oui"
                : "Non"
            }

            color={
              params.value
                ? "success"
                : "default"
            }

            variant={
              params.value
                ? "filled"
                : "outlined"
            }

            sx={{
              fontWeight: 800,
            }}
          />
        ),
    },


    {
      field:
        "actions",

      headerName:
        "Actions",

      width: 185,

      sortable: false,

      filterable: false,

      disableColumnMenu:
        true,

      renderCell:
        (params) => (
          <Stack
            direction="row"

            spacing={0.65}
          >
            <Button
              size="small"

              variant="contained"

              disableElevation

              onClick={() =>
                openEditDialog(
                  params.row
                )
              }

              disabled={
                !permissions.can_update
              }

              sx={{
                minWidth: 78,
              }}
            >
              Modifier
            </Button>


            <Button
              size="small"

              color="error"

              variant="outlined"

              onClick={() =>
                handleDeactivate(
                  params.row
                )
              }

              disabled={
                !permissions.can_delete ||
                !params.row.is_active
              }

              sx={{
                minWidth: 90,
              }}
            >
              Désactiver
            </Button>
          </Stack>
        ),
    },
  ]


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius: 2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap: 2,

            rowGap: 1.25,

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs:
                    "1.65rem",

                  sm:
                    "1.95rem",

                  md:
                    "2.1rem",
                },
              }}
            >
              Véhicules
            </Typography>


            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              Gérez les véhicules utilisés pour les
              livraisons, leur capacité, leur disponibilité
              et leur consommation de carburant.
            </Typography>
          </Box>


          <Button
            variant="contained"

            disableElevation

            onClick={
              openCreateDialog
            }

            disabled={
              !permissions.can_create
            }

            sx={{
              minHeight: 40,

              px: 2.2,

              minWidth: {
                xs: "100%",
                sm: 180,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouveau véhicule
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,

            alignItems: "center",

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          La consommation réelle en L/100 km est utilisée
          pour estimer automatiquement le carburant nécessaire
          et le coût logistique d'une livraison.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                md:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Véhicules"

            value={
              summary.total ||
              0
            }

            caption="Parc enregistré"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <DirectionsCarIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Disponibles"

            value={
              summary.available ||
              0
            }

            caption="Prêts à sortir"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleIcon fontSize="small" />
            }
          />


          <MetricCard
            label="En livraison"

            value={
              summary.in_delivery ||
              0
            }

            caption="Actuellement affectés"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <LocalShippingIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Maintenance"

            value={
              summary.maintenance ||
              0
            }

            caption="À ne pas affecter"

            accent="#ea580c"

            background="rgba(234, 88, 12, 0.09)"

            icon={
              <BuildIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "minmax(240px, 1.5fr) minmax(170px, 0.8fr) minmax(140px, 0.65fr) auto auto",
                },

              gap: 1,

              alignItems:
                "center",
            }}
          >
            <TextField
              size="small"

              label="Recherche"

              placeholder="Plaque, marque, modèle..."

              value={
                filters.search
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    search:
                      event.target.value,
                  })
                )
              }

              fullWidth
            />


            <TextField
              select

              size="small"

              label="Statut"

              value={
                filters.status
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    status:
                      event.target.value,
                  })
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {(formData.statuses || []).map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }

                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select

              size="small"

              label="Actif"

              value={
                filters.is_active
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    is_active:
                      event.target.value,
                  })
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="true">
                Oui
              </MenuItem>

              <MenuItem value="false">
                Non
              </MenuItem>
            </TextField>


            <Button
              variant="contained"

              disableElevation

              onClick={() =>
                loadVehicles()
              }

              disabled={
                loading
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 95,
                },
              }}
            >
              Appliquer
            </Button>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              disabled={
                loading
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 115,
                },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            VEHICLE REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            minWidth: 0,

            overflow: "hidden",

            borderRadius: 3.5,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* REGISTER HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Parc véhicules
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Véhicules, capacité, consommation et disponibilité
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${vehicles.length} véhicule${
                vehicles.length > 1
                  ? "s"
                  : ""
              }`}

              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflow: "hidden",

                bgcolor: "#ffffff",
              }}
            >
              <DataGrid
                autoHeight

                rows={
                  vehicles
                }

                columns={
                  columns
                }

                loading={
                  loading
                }

                disableRowSelectionOnClick

                disableColumnSelector

                pagination

                paginationModel={
                  paginationModel
                }

                onPaginationModelChange={
                  setPaginationModel
                }

                pageSizeOptions={[
                  ROWS_PER_PAGE,
                ]}

                rowHeight={62}

                columnHeaderHeight={48}

                localeText={{
                  noRowsLabel:
                    "Aucun véhicule enregistré",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}

                sx={{
                  border: 0,

                  width: "100%",

                  minWidth: 0,

                  color: "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor:
                        "#f8fafc",

                      px: 1.05,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight: 900,

                      color:
                        "#334155",

                      fontSize:
                        "0.78rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      px: 1.05,

                      minWidth: 0,

                      display: "flex",

                      alignItems:
                        "center",

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight: 50,

                      bgcolor:
                        "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE CARDS
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des véhicules...
                  </Typography>
                </Box>
              ) : paginatedVehicles.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun véhicule trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedVehicles.map(
                    (vehicle) => (
                      <Paper
                        key={
                          vehicle.id
                        }

                        variant="outlined"

                        sx={{
                          p: {
                            xs: 1.4,
                            sm: 1.6,
                          },

                          borderRadius: 3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={1.35}
                        >

                          {/* TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {vehicle.plate_number ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {vehicle.brand ||
                                  "Marque non renseignée"}{" "}

                                {vehicle.model ||
                                  ""}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              label={
                                statusLabels[
                                  vehicle.status
                                ] ||
                                vehicle.status ||
                                "-"
                              }

                              color={
                                statusColors[
                                  vehicle.status
                                ] ||
                                "default"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* TYPE */}

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            {getVehicleTypeLabel(
                              vehicle.vehicle_type,
                              formData.vehicle_types
                            )}
                          </Typography>


                          {/* CAPACITY + FUEL */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 1,
                            }}
                          >
                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius:
                                  2.5,

                                bgcolor:
                                  "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Capacité estimée
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {formatNumber(
                                  vehicle.capacity_units
                                )}
                              </Typography>
                            </Paper>


                            <Paper
                              variant="outlined"

                              sx={{
                                p: 1,

                                borderRadius:
                                  2.5,

                                bgcolor:
                                  "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Consommation
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {formatNumber(
                                  vehicle.fuel_consumption_l_100km,
                                  2
                                )}{" "}
                                L/100 km
                              </Typography>
                            </Paper>
                          </Box>


                          {/* ACTIVE */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              alignItems:
                                "center",

                              gap: 0.8,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              Enregistrement actif
                            </Typography>

                            <Chip
                              size="small"

                              label={
                                vehicle.is_active
                                  ? "Oui"
                                  : "Non"
                              }

                              color={
                                vehicle.is_active
                                  ? "success"
                                  : "default"
                              }

                              variant={
                                vehicle.is_active
                                  ? "filled"
                                  : "outlined"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* NOTES */}

                          {vehicle.notes ? (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Notes
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.2,

                                  lineHeight:
                                    1.5,

                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {vehicle.notes}
                              </Typography>
                            </Box>
                          ) : null}


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.8,
                            }}
                          >
                            <Button
                              size="small"

                              variant="contained"

                              disableElevation

                              onClick={() =>
                                openEditDialog(
                                  vehicle
                                )
                              }

                              disabled={
                                !permissions.can_update
                              }
                            >
                              Modifier
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              color="error"

                              onClick={() =>
                                handleDeactivate(
                                  vehicle
                                )
                              }

                              disabled={
                                !permissions.can_delete ||
                                !vehicle.is_active
                              }
                            >
                              Désactiver
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  vehicles.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius:
                    2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight:
                        50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display:
                        "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display:
                        "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          VEHICLE DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          closeDialog
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "90dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.4,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedVehicle
              ? "Modifier le véhicule"
              : "Nouveau véhicule"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedVehicle
              ? "Modifiez les caractéristiques et la disponibilité de ce véhicule."
              : "Ajoutez un véhicule au parc logistique et renseignez sa consommation réelle."}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* INFO */}

            <Alert
              severity="info"

              variant="outlined"

              sx={{
                borderRadius: 3,
              }}
            >
              Renseignez toujours la consommation réelle en
              L/100 km. Elle permet au module logistique de
              calculer automatiquement le carburant nécessaire
              et son coût estimé. Une valeur égale à 0 n'est pas
              acceptée.
            </Alert>


            {/* IDENTIFICATION */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Identification
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Plaque"

                  value={
                    form.plate_number
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "plate_number",
                      event.target.value.toUpperCase()
                    )
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  placeholder="Ex. 1234 AB 01"
                />


                <TextField
                  select

                  size="small"

                  label="Type de véhicule"

                  value={
                    form.vehicle_type
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "vehicle_type",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {(formData.vehicle_types || []).map(
                    (type) => (
                      <MenuItem
                        key={
                          type.value
                        }

                        value={
                          type.value
                        }
                      >
                        {type.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Marque"

                  value={
                    form.brand
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "brand",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  placeholder="Ex. Toyota"
                />


                <TextField
                  size="small"

                  label="Modèle"

                  value={
                    form.model
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "model",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  placeholder="Ex. Hilux"
                />
              </Box>
            </Paper>


            {/* LOGISTICS PARAMETERS */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                bgcolor:
                  "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Paramètres logistiques
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Capacité estimée"

                  type="number"

                  value={
                    form.capacity_units
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "capacity_units",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 1,
                    },
                  }}

                  helperText="Nombre d'unités ou capacité approximative."
                />


                <TextField
                  size="small"

                  label="Consommation L/100 km"

                  type="number"

                  value={
                    form.fuel_consumption_l_100km
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "fuel_consumption_l_100km",
                      event.target.value
                    )
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  error={
                    form.fuel_consumption_l_100km !==
                      "" &&
                    toNumber(
                      form.fuel_consumption_l_100km
                    ) <= 0
                  }

                  helperText="Obligatoire pour l'estimation carburant."

                  slotProps={{
                    htmlInput: {
                      min: 0.1,
                      step: 0.1,
                    },
                  }}
                />
              </Box>
            </Paper>


            {/* STATUS */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Disponibilité
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  select

                  size="small"

                  label="Statut opérationnel"

                  value={
                    form.status
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "status",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {(formData.statuses || []).map(
                    (status) => (
                      <MenuItem
                        key={
                          status.value
                        }

                        value={
                          status.value
                        }
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select

                  size="small"

                  label="Enregistrement actif"

                  value={
                    String(
                      form.is_active
                    )
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "is_active",
                      event.target.value ===
                        "true"
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="true">
                    Oui
                  </MenuItem>

                  <MenuItem value="false">
                    Non
                  </MenuItem>
                </TextField>
              </Box>
            </Paper>


            {/* NOTES */}

            <TextField
              label="Notes"

              value={
                form.notes
              }

              onChange={(
                event
              ) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }

              placeholder="Informations complémentaires, état mécanique, contraintes particulières..."
            />
          </Stack>
        </DialogContent>


        {/* =================================================
            ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              closeDialog
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"

            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedVehicle
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          onClose={
            closeSnackbar
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default VehiclesPage